import { PlayerScenarioRunnerAPI } from "@manager/PlayerManager";
import { inject, injectable } from "inversify";
import ReactDOM from "react-dom";
import { VintomError } from "../../api/model/error/VintomError";
import { ScenarioElement } from "../../model/playerConfiguration/ScenarioElement";
import { Subtitle } from "../../model/playerConfiguration/Subtitle";
import { PlaybackData } from "../../model/playerState/PlaybackData";
import { ScenarioElementPlayOptions } from "../../model/scenarioElementPlayOptions/ScenarioElementPlayOptions";
import { ScenarioGlobalState } from "../../model/scenarioGlobalState/ScenarioGlobalState";
import { SubtitlesText } from "../../model/subtitles/SubtitlesText";
import { TYPES } from "../di/types";
import { ResourcesCache } from "../loader/scenarioLoader/ResourcesCache";
import { PreferencesStorage } from "../preferencesStorage/PreferencesStorage";
import { ControlsLayerManager } from "./layerManager/ControlsLayerManager";
import { CTALayerManager } from "./layerManager/CTALayerManager";
import { DependentAnimationLayerManager } from "./layerManager/DependentAnimationLayerManager";
import { PlayerContainerManager } from "./layerManager/PlayerContainerManager";
import { PosterLayerManager } from "./layerManager/PosterLayerManager";
import { VideoLayerManager } from "./layerManager/VideoLayerManager";
import { ScenarioExecutor } from "./ScenarioExecutor";
import { MusicManager } from "@manager/scenarioRunner/musicManager/MusicManager";

@injectable()
export class VideoDrivenScenarioExecutorImpl implements ScenarioExecutor {

  private playerAPI: PlayerScenarioRunnerAPI;
  private animationFrameHandle: number;
  private scenarioGlobalState: ScenarioGlobalState;

  constructor(
    @inject(TYPES.scenarioRunner.layerManager.PlayerContainerManager) private playerContainerManager: PlayerContainerManager,
    @inject(TYPES.scenarioRunner.layerManager.VideoLayerManager) private videoLayerManager: VideoLayerManager,
    @inject(TYPES.scenarioRunner.layerManager.DependentAnimationLayerManager) private animationLayerManager: DependentAnimationLayerManager,
    @inject(TYPES.scenarioRunner.layerManager.CTALayerManager) private ctaLayerManager: CTALayerManager,
    @inject(TYPES.scenarioRunner.layerManager.ControlsLayerManager) private controlsLayerManager: ControlsLayerManager,
    @inject(TYPES.scenarioRunner.layerManager.PosterLayerManager) private posterLayerManager: PosterLayerManager,
    @inject(TYPES.scenarioRunner.musicManager.MusicManager) private musicManager: MusicManager,
    @inject(TYPES.preferencesStorage.PreferencesStorage) private preferencesStorage: PreferencesStorage) {}

  setPlayerAPIDelegate(playerAPI: PlayerScenarioRunnerAPI) {
    this.playerAPI = playerAPI;
  }

  cancel(): void {
    if (this.animationFrameHandle !== undefined) {
      cancelAnimationFrame(this.animationFrameHandle);
    }
  }

  intializeAndRun(
    scenarioGlobalState: ScenarioGlobalState,
    scenarioResources: Promise<ResourcesCache>,
    options: ScenarioElementPlayOptions): Promise<void> {

    this.scenarioGlobalState = scenarioGlobalState;
    return this.initializeScenarioElement(scenarioGlobalState, scenarioResources, options.progress)
      .then(() => {
        this.runSyncLoop();
        if (options.isAutoplay) {
          this.controlsLayerManager.play();
        } else {
          this.controlsLayerManager.pause();
        }
      });
  }

  getPlaybackData(): PlaybackData {
    const isPlaying = this.videoLayerManager.getIsPlaying();
    const time = this.videoLayerManager.getCurrentSeekTime();
    const duration = this.videoLayerManager.getTotalTime();
    const volume = this.videoLayerManager.getVolume();

    return { isPlaying, time, duration, volume };
  }

  getScenarioGlobalState(): ScenarioGlobalState {
    return this.scenarioGlobalState;
  }

  play(): void {
    this.controlsLayerManager.play();
  }

  pause(): void {
    this.controlsLayerManager.pause();
  }

  performAction(ctaId: number, actionId: number, ...args: any[]): void {
    this.ctaLayerManager.performAction(ctaId, actionId, args);
  }

  jumpToScenarioElement(scenarioElement: ScenarioElement, progress: number): void {
    this.videoLayerManager.pause();
    cancelAnimationFrame(this.animationFrameHandle);
    this.posterLayerManager.showPosterOrSnapshot(scenarioElement);
    setTimeout(() => this.playerAPI.loadScenarioElement(scenarioElement, { isAutoplay: true, progress }), 50);
  }

  private initializeScenarioElement(scenarioGlobalState: ScenarioGlobalState, scenarioResources: Promise<ResourcesCache>, progress: number):
    Promise<void> {

    this.resetObservers();

    const scenarioPath = scenarioGlobalState.scenarioPath;
    this.posterLayerManager.newScenarioElement(scenarioGlobalState);
    this.videoLayerManager.newScenarioElement(scenarioPath);
    this.controlsLayerManager.newScenarioElement(scenarioPath);
    this.controlsLayerManager.showLoader();
    this.ctaLayerManager.newScenarioElement(scenarioGlobalState);
    this.animationLayerManager.newScenarioElement(scenarioPath);

    return scenarioResources.then((resourcesCache) => {

      this.posterLayerManager.resourcesLoaded(resourcesCache);
      if (!this.posterLayerManager.getIsShowingPoster() && progress === 0) {
        this.posterLayerManager.showCachedPoster(scenarioPath.currentElement.poster);
      }

      const videoDuration = this.videoLayerManager.loadVideoAndGetDuration(progress);

      return videoDuration.then((duration) => {

        this.animationLayerManager.resourcesLoaded(resourcesCache, duration);

        this.ctaLayerManager.resourcesLoaded(resourcesCache, duration);

        this.videoLayerManager.changeVolume(scenarioGlobalState.volume);
        this.videoLayerManager.changeMuted(scenarioGlobalState.isMuted);
        if (scenarioGlobalState.scenarioPath.currentElement.hasSubtitles) {
          this.videoLayerManager.activateSubtitles(this.scenarioGlobalState.selectedSubtitles &&
            this.scenarioGlobalState.selectedSubtitles.source);
        }

        this.controlsLayerManager.setTotalTime(duration);
        this.controlsLayerManager.changeVolume(scenarioGlobalState.volume);
        this.controlsLayerManager.changeIsMuted(scenarioGlobalState.isMuted);
        if (scenarioGlobalState.scenarioPath.currentElement.hasSubtitles) {
          this.controlsLayerManager.selectSubtitleSource(this.scenarioGlobalState.selectedSubtitles);
        }

      });
    });
  }

  private resetObservers() {
    this.playerContainerManager.registerObserver({
      onPlayerContextMenu: (x: number, y: number) => this.controlsLayerManager.showPlayerInfoTooltip(x, y),
      onPlayerInteractionEnd: () => this.controlsLayerManager.userInteractionEnded(),
      onPlayerInteraction: () => this.controlsLayerManager.userInteractedWithPlayer(),
      onPlayerClick: () => {
        this.controlsLayerManager.userClickedPlayer();
      }
    });

    this.videoLayerManager.registerObserver({
      onEnded: () => {
        this.controlsLayerManager.scenarioElementEnded();
        this.posterLayerManager.scenarioElementEnded();
        this.playerAPI.onScenarioElementEnd();
        // this.musicManager.onScenarioElementEnd();
      },
      onError: (error) => this.playerAPI.onError(error),
      onStarted: () => this.playerAPI.onScenarioElementStart(),
      onSubtitlesChange: (subtitles: SubtitlesText) => this.controlsLayerManager.displaySubtitles(subtitles),
      onTotalTimeChange: (totalTime) => {
        this.animationLayerManager.setTotalTime(totalTime);
        this.ctaLayerManager.setTotalTime(totalTime);
        this.controlsLayerManager.setTotalTime(totalTime);
      },
      onPlay: () => this.playerAPI.onPlay(),
      onPause: () => this.playerAPI.onPause(),
    });

    this.controlsLayerManager.registerObserver({
      onPause: () => { 
        this.videoLayerManager.pause(); 
        this.musicManager.pause();
      },
      onSeekStart: () => {
        this.videoLayerManager.pause();
        this.musicManager.pause();
        this.playerAPI.onSeek();
      },
      onPlay: () => {
        this.videoLayerManager.play();
        this.musicManager.play();
      },  
      onReloadFrame: () => this.videoLayerManager.reloadFrame(),
      onSeekTo: (time) => {
        this.videoLayerManager.moveTo(time);
      },
      onVolumeChange: (volume: number) => {
        this.preferencesStorage.saveVolumePreference(volume);
        this.scenarioGlobalState = this.scenarioGlobalState.withVolume(volume);
        this.videoLayerManager.changeVolume(volume);
      },
      onMutedChange: (isMuted: boolean) => {
        this.scenarioGlobalState = this.scenarioGlobalState.withMuted(isMuted);
        this.videoLayerManager.changeMuted(isMuted);
        // this.musicManager.(isMuted);
      },
      onSubtitleSourceSelected: (subtitle: Subtitle) => {
        this.preferencesStorage.saveSubtitlesPreference(!!subtitle, subtitle && subtitle.label);
        this.scenarioGlobalState = this.scenarioGlobalState.withSelectedSubtitles(subtitle);
        this.videoLayerManager.activateSubtitles(this.scenarioGlobalState.selectedSubtitles &&
          this.scenarioGlobalState.selectedSubtitles.source);
      },
      onJumpToScenarioElement: (scElement: ScenarioElement, progress: number) => this.jumpToScenarioElement(scElement, progress),
      onToggleFullscreen: () => this.playerContainerManager.toggleFullscreen(),
      onSendError: (error: VintomError) => this.playerAPI.logUserError(error)
    });

    this.ctaLayerManager.registerObserver({
      onCTAShow: (cta) => this.playerAPI.onCTAShow(cta)
    });
  }

  private runSyncLoop() {
    this.animationFrameHandle = requestAnimationFrame(() => {
      try {
        const currentSeekTime = this.videoLayerManager.getCurrentSeekTime();
        const currentFrameTime = this.videoLayerManager.getCurrentFrameTime();
        const bufferedTime = this.videoLayerManager.getBufferedTime();
        const totalTime = this.videoLayerManager.getTotalTime();
        const bandwidth = this.videoLayerManager.getBandwidth();
        const qualityLevels = this.videoLayerManager.getQualityLevels();
        const isFullyLoaded = this.videoLayerManager.getIsFullyLoaded();
        const isPlaying = this.videoLayerManager.getIsPlaying();
        const isWaiting = this.videoLayerManager.getIsWaiting();

        ReactDOM.unstable_batchedUpdates(() => {

          this.ctaLayerManager.moveTo(currentFrameTime);

          this.controlsLayerManager.moveTo(currentSeekTime);
          this.controlsLayerManager.updateBufferedTime(bufferedTime);
          this.controlsLayerManager.updateQualityLevels(qualityLevels);
          this.controlsLayerManager.updateBandwidth(bandwidth);

          this.playerAPI.onProgress(currentFrameTime / totalTime);

          if (isFullyLoaded) {
            this.animationLayerManager.moveTo(currentFrameTime);
          }

          if (isFullyLoaded && this.videoLayerManager.getIsPlaying()) {
            this.posterLayerManager.hidePoster();
          }

          if (isWaiting) {
            this.controlsLayerManager.showLoader();
          } else {
            this.controlsLayerManager.hideLoader();
          }
        });

        this.runSyncLoop();
      } catch (e) {
        this.playerAPI.onError(new VintomError("runtimeError", e.stack.toString()));
      }

    });
  }

}
